import {
  Home,
  Landmark,
  Percent,
  Wallet,
  Receipt,
  PiggyBank,
} from "lucide-react";

import { ACTIONS, FIELD_TYPES, RESULT_TYPES, CHARTS } from "@/utils/calculatorSchema";

import { formatCurrency } from "@/utils/finance";

import InsightCard from "@/components/calculator/InsightCard";

import DataTable from "@/components/calculator/DataTable";

const ltvStampDutyCalculatorConfig = {
  id: "ltv-stamp-duty",

  title: "LTV & Stamp Duty Calculator",

  shortTitle: "LTV & Stamp Duty",

  icon: Home,

  description:
    "Estimate your maximum eligible loan based on the Loan-to-Value (LTV) ratio and calculate the upfront cash required, including down payment, stamp duty, and registration charges.",

  keywords: [
    "LTV Calculator",
    "Loan To Value Calculator",
    "Stamp Duty Calculator",
    "Registration Charges Calculator",
    "Home Buying Cost Calculator",
  ],

  fields: [
    {
      key: "propertyValue",
      label: "Property Value",
      type: FIELD_TYPES.CURRENCY,
      defaultValue: 7000000,
      min: 100000,
      max: 500000000,
      step: 100000,
      required: true,
    },

    {
      key: "ltvPercentage",
      label: "LTV Ratio",
      type: FIELD_TYPES.PERCENT,
      defaultValue: 80,
      min: 60,
      max: 90,
      step: 1,
      suffix: "%",
    },

    {
      key: "stampDutyPercentage",
      label: "Stamp Duty",
      type: FIELD_TYPES.PERCENT,
      defaultValue: 5,
      min: 2,
      max: 8,
      step: 0.1,
      suffix: "%",
    },

    {
      key: "registrationPercentage",
      label: "Registration Charges",
      type: FIELD_TYPES.PERCENT,
      defaultValue: 1,
      min: 0,
      max: 2,
      step: 0.1,
      suffix: "%",
    },

    {
      key: "registrationCap",
      label: "Registration Cap",
      type: FIELD_TYPES.CURRENCY,
      defaultValue: 30000,
      min: 1000,
      max: 5000000,
      step: 1000,
    },
  ],

  summaryCards: [
    {
      title: "Loan Eligible",
      key: "loanEligible",
      type: RESULT_TYPES.CURRENCY,
      description: "Maximum loan based on selected LTV.",
      icon: Home,
    },

    {
      title: "Down Payment",
      key: "downPayment",
      type: RESULT_TYPES.CURRENCY,
      description: "Amount payable from your own funds.",
      icon: Wallet,
    },

    {
      title: "LTV Ratio",
      key: "ltvPercentage",
      type: RESULT_TYPES.PERCENT,
      description: "Selected Loan-to-Value ratio.",
      icon: Percent,
    },

    {
      title: "Stamp Duty",
      key: "stampDuty",
      type: RESULT_TYPES.CURRENCY,
      description: "Estimated stamp duty payable.",
      icon: Landmark,
    },

    {
      title: "Registration",
      key: "registration",
      type: RESULT_TYPES.CURRENCY,
      description: "Estimated registration charges.",
      icon: Receipt,
    },

    {
      title: "Upfront Cash",
      key: "totalUpfrontCash",
      type: RESULT_TYPES.CURRENCY,
      description: "Total amount required before loan disbursal.",
      icon: PiggyBank,
    },
  ],

  charts: [
    {
      id: "property-financing",
      title: "Property Financing",
      type: CHARTS.PIE,

      resultsKey: "propertyFinancing",

      dataKey: "value",
      nameKey: "name",

      description:
        "Shows how the property's value is split between the loan amount and down payment.",
    },

    {
      id: "upfront-cost-breakdown",
      title: "Upfront Cost Breakdown",
      type: CHARTS.PIE,

      resultsKey: "upfrontCostBreakdown",

      dataKey: "value",
      nameKey: "name",

      description:
        "Distribution of the upfront costs required for the purchase.",
    },
  ],

  tables: [
    {
      dataKey: "costBreakdown",

      title: "Cost Breakdown",

      component: DataTable,

      columns: [
        {
          key: "label",
          label: "Item",
          align: "left",
        },

        {
          key: "value",
          label: "Amount",
          type: RESULT_TYPES.CURRENCY,
        },
      ],
    },
  ],

  metadata: {
    category: "Home Loan",
    readingTime: "2 min",
  },

  actions: [ACTIONS.RESET, ACTIONS.COPY, ACTIONS.EXCEL],

  calculate(values) {
    const {
      propertyValue,
      ltvPercentage,
      stampDutyPercentage,
      registrationPercentage,
      registrationCap,
    } = values;

    const loanEligible = propertyValue * (ltvPercentage / 100);

    const downPayment = propertyValue - loanEligible;

    const stampDuty = propertyValue * (stampDutyPercentage / 100);

    const registration = Math.min(
      propertyValue * (registrationPercentage / 100),
      registrationCap,
    );

    const totalUpfrontCash = downPayment + stampDuty + registration;

    const summary = {
      propertyValue,

      loanEligible,

      downPayment,

      ltvPercentage,

      stampDuty,

      registration,

      totalUpfrontCash,

      stampDutyPercentage,

      registrationPercentage,

      registrationCap,
    };

    return {
      summary,

      charts: {
        propertyFinancing: [
          {
            name: "Loan Eligible",
            value: loanEligible,
          },
          {
            name: "Down Payment",
            value: downPayment,
          },
        ],

        upfrontCostBreakdown: [
          {
            name: "Down Payment",
            value: downPayment,
          },
          {
            name: "Stamp Duty",
            value: stampDuty,
          },
          {
            name: "Registration",
            value: registration,
          },
        ],
      },

      tables: {
        costBreakdown: [
          {
            label: "Property Value",
            value: propertyValue,
          },
          {
            label: "Loan Eligible",
            value: loanEligible,
          },
          {
            label: "Down Payment",
            value: downPayment,
          },
          {
            label: "Stamp Duty",
            value: stampDuty,
          },
          {
            label: "Registration Charges",
            value: registration,
          },
          {
            label: "Total Upfront Cash",
            value: totalUpfrontCash,
          },
        ],
      },
    };
  },

  exportExcel: ({ results }) => ({
    "Cost Breakdown": results.tables.costBreakdown ?? [],
  }),

  renderInsights({ results }) {
    const { summary } = results;

    const downPaymentPercentage = 100 - summary.ltvPercentage;

    return (
      <>
        <InsightCard type="success" title="Loan Eligibility">
          Based on an LTV ratio of <strong>{summary.ltvPercentage}%</strong>,
          you are eligible for a maximum loan of{" "}
          <strong>{formatCurrency(summary.loanEligible)}</strong>.
        </InsightCard>

        <InsightCard type="info" title="Down Payment">
          You'll need to arrange approximately{" "}
          <strong>{downPaymentPercentage}%</strong> of the property's value (
          <strong>{formatCurrency(summary.downPayment)}</strong>) from your own
          funds.
        </InsightCard>

        <InsightCard type="warning" title="Upfront Cash Requirement">
          Before the loan is disbursed, you'll need a total of{" "}
          <strong>{formatCurrency(summary.totalUpfrontCash)}</strong> for the
          down payment, stamp duty and registration charges.
        </InsightCard>
      </>
    );
  },
};

export default ltvStampDutyCalculatorConfig;
